/**
 * Edit Format Tool - Factory (with image + write tracking)
 */
import { ToolBase, ToolDescription } from "../../../../../agents/base";
import { buildWriteEntry, collectImagesFromResult, } from "../../../../writeTracking";
import { EditFormatParamsSchema, } from "./editFormat.schema";
const SYSTEM_PROMPT_DESCRIPTION = `### editFormat
Apply workbook formatting (fills, fonts, borders, column widths, row heights, conditional formatting).

**When to use:**
- Changing cell backgrounds, text colours, font styles
- Setting column widths or row heights
- Adding borders around ranges
- Applying conditional formatting rules
- Copying formatting from one range to others

**When NOT to use:**
- Changing cell values or formulas (use editContent)
- Number formats like currency/percentage (use editContent numberFormats)

Requires \`targetRange\` in A1-notation (e.g. "A1:F20").

Dimension units:
- \`columnWidths[].width\` is in **pixels** (not Excel character units, not points)
- \`rowHeights[].height\` is in **pixels** (backend converts internally for host APIs)
- Practical starting values: body rows ~20-28 px, readable table columns ~80-220 px

Color policy (match backend defaults unless user explicitly asks otherwise):
- primary header: [31,73,125], secondary fill: [217,217,217], highlight: [221,235,247]
- input/check fills: [255,255,204], [224,224,224]
- input/check borders: [160,160,160], [112,112,112]
- text/border neutrals: [0,0,0], [255,255,255]
- Avoid introducing gold/yellow accent rows unless explicitly requested.

Canonical formatting examples:
- Header row run: \`cellFill.color=[31,73,125]\`, \`fontColor=[255,255,255]\`, \`bold=true\`, \`bottomBorder.color=[0,0,0]\`
- Body rows (default): no fill or subtle highlight \`[221,235,247]\` only when explicitly requested
- Totals row run: \`cellFill.color=[217,217,217]\`, \`bold=true\`, \`topBorder.color=[0,0,0]\`

Use minimal tool calls with contiguous ranges. Preserve formatting continuity across the requested range (including empty cells in-band).

For \`customRangeFormatting\`, prefer canonical shape:
- \`addresses\` (numeric row/column bounds)
- \`cellFill\`, \`font*\`, \`alignment\`, and border specs.
Legacy shape (\`range\` + nested \`font\`/\`fill\`) is accepted and normalized.`;
const STREAM_BATCH_SIZE = 1;
const STREAM_FLUSH_MS = 200;
const STREAM_LOG_PREFIX = "[EditFormatStream]";
// Hard toggle for latency A/B testing.
// Set to false to force single-shot editFormat calls.
const ENABLE_EDIT_FORMAT_STREAMING = true;
const BACKEND_STYLE_PALETTE = {
    primaryHeader: [31, 73, 125],
    secondaryFill: [217, 217, 217],
    highlightFill: [221, 235, 247],
    inputFill: [255, 255, 204],
    checkFill: [224, 224, 224],
    inputBorder: [160, 160, 160],
    checkBorder: [112, 112, 112],
    black: [0, 0, 0],
    white: [255, 255, 255],
};
const BACKEND_STYLE_COLOR_KEYS = new Set(Object.values(BACKEND_STYLE_PALETTE).map((rgb) => rgb.join(",")));
function summarizeColumnWidths(columnWidths) {
    if (!columnWidths?.length)
        return "none";
    const sample = columnWidths
        .slice(0, 12)
        .map((item) => `C${item.column}=${item.width}px${item.allow_expand === false ? " (no-expand)" : ""}`)
        .join(", ");
    return columnWidths.length > 12 ? `${sample}, ... (+${columnWidths.length - 12} more)` : sample;
}
function summarizeRowHeights(rowHeights) {
    if (!rowHeights?.length)
        return "none";
    const sample = rowHeights
        .slice(0, 12)
        .map((item) => `R${item.row}=${item.height}px`)
        .join(", ");
    return rowHeights.length > 12 ? `${sample}, ... (+${rowHeights.length - 12} more)` : sample;
}
function addColorKey(colorKeys, value) {
    const rgb = normalizeRgbColor(value);
    if (!rgb)
        return;
    colorKeys.add(rgb.join(","));
}
function summarizeColorKeys(colorKeys) {
    const keys = Array.from(colorKeys.values());
    if (!keys.length)
        return "none";
    const sample = keys.slice(0, 16).map((key) => `[${key}]`).join(", ");
    return keys.length > 16 ? `${sample}, ... (+${keys.length - 16} more)` : sample;
}
function summarizeColorUsage(request) {
    const usedColorKeys = new Set();
    const runs = request.customRangeFormatting || [];
    for (const run of runs) {
        addColorKey(usedColorKeys, run.cellFill?.color);
        addColorKey(usedColorKeys, run.fontColor);
        addColorKey(usedColorKeys, run.leftBorder?.color);
        addColorKey(usedColorKeys, run.rightBorder?.color);
        addColorKey(usedColorKeys, run.topBorder?.color);
        addColorKey(usedColorKeys, run.bottomBorder?.color);
    }
    const conditionalRules = request.conditionalFormatting || [];
    for (const rule of conditionalRules) {
        const ruleObj = asObject(rule);
        addColorKey(usedColorKeys, ruleObj.minColor);
        addColorKey(usedColorKeys, ruleObj.midColor);
        addColorKey(usedColorKeys, ruleObj.maxColor);
        const formatObj = asObject(ruleObj.format);
        addColorKey(usedColorKeys, formatObj.backgroundColor);
        addColorKey(usedColorKeys, formatObj.fontColor);
    }
    const offPalette = new Set();
    for (const key of usedColorKeys) {
        if (!BACKEND_STYLE_COLOR_KEYS.has(key)) {
            offPalette.add(key);
        }
    }
    return {
        used: summarizeColorKeys(usedColorKeys),
        offPalette: summarizeColorKeys(offPalette),
    };
}
function asObject(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
function unwrapRequest(raw) {
    const obj = asObject(raw);
    const nested = obj.request;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
        return nested;
    }
    return obj;
}
function resolveSessionId(raw, fallback) {
    const obj = asObject(raw);
    return (typeof obj.sessionId === "string" ? obj.sessionId : undefined) || fallback;
}
function toInt(value) {
    return typeof value === "number" && Number.isFinite(value) ? Math.trunc(value) : undefined;
}
function asString(value) {
    return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
}
function columnLettersToIndex(letters) {
    let result = 0;
    const upper = letters.toUpperCase();
    for (let i = 0; i < upper.length; i += 1) {
        result = result * 26 + (upper.charCodeAt(i) - 64);
    }
    return result;
}
function parseA1Cell(cell) {
    const match = cell.match(/^([A-Za-z]+)(\d+)$/);
    if (!match) {
        return null;
    }
    const col = columnLettersToIndex(match[1]);
    const row = Number.parseInt(match[2], 10);
    if (!Number.isFinite(row) || row <= 0 || col <= 0) {
        return null;
    }
    return { row, col };
}
function parseRangeAddress(rangeRef) {
    const withoutSheet = rangeRef.includes("!")
        ? rangeRef.slice(rangeRef.lastIndexOf("!") + 1)
        : rangeRef;
    const [startRaw, endRaw] = withoutSheet.split(":");
    const start = parseA1Cell(startRaw);
    const end = parseA1Cell(endRaw || startRaw);
    if (!start || !end) {
        return null;
    }
    return {
        startColumn: Math.min(start.col, end.col),
        endColumn: Math.max(start.col, end.col),
        startRow: Math.min(start.row, end.row),
        endRow: Math.max(start.row, end.row),
    };
}
function normalizeRgbColor(value) {
    if (Array.isArray(value) && value.length === 3) {
        const rgb = value.map((part) => toInt(part));
        if (rgb.every((part) => typeof part === "number" && part >= 0 && part <= 255)) {
            return rgb;
        }
    }
    const raw = asString(value);
    if (!raw) {
        return undefined;
    }
    const compact = raw.replace(/^#/, "");
    if (/^[0-9A-Fa-f]{6}$/.test(compact)) {
        return [
            Number.parseInt(compact.slice(0, 2), 16),
            Number.parseInt(compact.slice(2, 4), 16),
            Number.parseInt(compact.slice(4, 6), 16),
        ];
    }
    const rgbMatch = raw.match(/^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i);
    if (!rgbMatch) {
        return undefined;
    }
    const r = Number.parseInt(rgbMatch[1], 10);
    const g = Number.parseInt(rgbMatch[2], 10);
    const b = Number.parseInt(rgbMatch[3], 10);
    if ([r, g, b].every((part) => Number.isFinite(part) && part >= 0 && part <= 255)) {
        return [r, g, b];
    }
    return undefined;
}
function normalizeBorderStyle(value) {
    const raw = asString(value);
    if (!raw) {
        return undefined;
    }
    const normalized = raw.toLowerCase();
    const map = {
        thin: "Solid",
        medium: "Solid",
        thick: "Solid",
        solid: "Solid",
        continuous: "Solid",
        dashed: "Dashed",
        dash: "Dashed",
        dotted: "Dotted",
        dot: "Dotted",
        double: "Double",
    };
    return map[normalized];
}
function normalizeBorder(value) {
    const obj = asObject(value);
    if (!Object.keys(obj).length) {
        return undefined;
    }
    const border = {};
    if (typeof obj.clear === "boolean") {
        border.clear = obj.clear;
    }
    const color = normalizeRgbColor(obj.color);
    if (color) {
        border.color = color;
    }
    const style = normalizeBorderStyle(obj.style);
    if (style) {
        border.style = style;
    }
    return Object.keys(border).length > 0 ? border : undefined;
}
function normalizeFill(value) {
    const obj = asObject(value);
    if (!Object.keys(obj).length) {
        return undefined;
    }
    const fill = {};
    if (typeof obj.clear === "boolean") {
        fill.clear = obj.clear;
    }
    const color = normalizeRgbColor(obj.color);
    if (color) {
        fill.color = color;
    }
    return Object.keys(fill).length > 0 ? fill : undefined;
}
function normalizeAlignment(value) {
    const source = typeof value === "string" ? value : asString(asObject(value).horizontal);
    if (!source) {
        return undefined;
    }
    const normalized = source.trim().toLowerCase();
    const map = {
        left: "Left",
        center: "Center",
        right: "Right",
        "center across selection": "Center Across Selection",
        centeracrossselection: "Center Across Selection",
    };
    return map[normalized];
}
function normalizeAddress(value) {
    const obj = asObject(value);
    const startColumn = toInt(obj.startColumn);
    const endColumn = toInt(obj.endColumn);
    const startRow = toInt(obj.startRow);
    const endRow = toInt(obj.endRow);
    if (!startColumn ||
        !endColumn ||
        !startRow ||
        !endRow ||
        startColumn <= 0 ||
        endColumn <= 0 ||
        startRow <= 0 ||
        endRow <= 0) {
        return null;
    }
    return {
        startColumn,
        endColumn,
        startRow,
        endRow,
    };
}
function normalizeCustomRangeFormatting(run) {
    const obj = asObject(run);
    const fromAddresses = Array.isArray(obj.addresses)
        ? obj.addresses
            .map((address) => normalizeAddress(address))
            .filter((address) => !!address)
        : [];
    const legacyRange = asString(obj.rangeRef) || asString(obj.range);
    const legacyAddress = legacyRange ? parseRangeAddress(legacyRange) : null;
    const addresses = fromAddresses.length > 0 ? fromAddresses : legacyAddress ? [legacyAddress] : [];
    if (addresses.length === 0) {
        return null;
    }
    const fontObj = asObject(obj.font);
    const fillObj = asObject(obj.fill);
    const normalized = { addresses };
    normalized.leftBorder = normalizeBorder(obj.leftBorder);
    normalized.rightBorder = normalizeBorder(obj.rightBorder);
    normalized.topBorder = normalizeBorder(obj.topBorder);
    normalized.bottomBorder = normalizeBorder(obj.bottomBorder);
    normalized.cellFill = normalizeFill(obj.cellFill) || normalizeFill(fillObj);
    normalized.fontName = asString(obj.fontName) || asString(fontObj.name);
    const fontSize = toInt(obj.fontSize) ?? toInt(fontObj.size);
    if (typeof fontSize === "number") {
        normalized.fontSize = fontSize;
    }
    normalized.fontColor = normalizeRgbColor(obj.fontColor) || normalizeRgbColor(fontObj.color);
    if (typeof obj.bold === "boolean") {
        normalized.bold = obj.bold;
    }
    else if (typeof fontObj.bold === "boolean") {
        normalized.bold = fontObj.bold;
    }
    if (typeof obj.italic === "boolean") {
        normalized.italic = obj.italic;
    }
    else if (typeof fontObj.italic === "boolean") {
        normalized.italic = fontObj.italic;
    }
    const underline = asString(obj.underline);
    if (underline &&
        ["Single", "Double", "SingleAccounting", "DoubleAccounting", "None"].includes(underline)) {
        normalized.underline = underline;
    }
    normalized.alignment = normalizeAlignment(obj.alignment);
    const indentLevel = toInt(obj.indentLevel);
    if (typeof indentLevel === "number" && indentLevel >= 0) {
        normalized.indentLevel = indentLevel;
    }
    normalized.numberFormat = asString(obj.numberFormat);
    const hasFormatting = !!(normalized.leftBorder ||
        normalized.rightBorder ||
        normalized.topBorder ||
        normalized.bottomBorder ||
        normalized.cellFill ||
        normalized.fontName ||
        typeof normalized.fontSize === "number" ||
        normalized.fontColor ||
        typeof normalized.bold === "boolean" ||
        typeof normalized.italic === "boolean" ||
        normalized.underline ||
        normalized.alignment ||
        typeof normalized.indentLevel === "number" ||
        normalized.numberFormat);
    return hasFormatting ? normalized : null;
}
function normalizeEditFormatRequest(raw) {
    const request = raw;
    const sheetName = asString(raw.sheetName);
    const rawTargetRange = asString(raw.targetRange) || request.targetRange;
    const normalizedTargetRange = sheetName && rawTargetRange && !rawTargetRange.includes("!")
        ? `${sheetName}!${rawTargetRange}`
        : rawTargetRange;
    const runs = Array.isArray(raw.customRangeFormatting)
        ? raw.customRangeFormatting
            .map((run) => normalizeCustomRangeFormatting(run))
            .filter((run) => !!run)
        : undefined;
    return {
        ...request,
        targetRange: normalizedTargetRange,
        customRangeFormatting: runs,
    };
}
export function createEditFormatTool(runner, fallbackSessionId) {
    return new EditFormatTool(runner, fallbackSessionId).toRuntimeToolDefinition();
}
export class EditFormatTool extends ToolBase {
    constructor(runner, fallbackSessionId) {
        super();
        this.runner = runner;
        this.fallbackSessionId = fallbackSessionId;
        this.name = "editFormat";
        this.fieldsToCheck = [
            { name: "uiDescription", type: "string", list_status: false, order: 0 },
            { name: "sheetName", type: "string", list_status: false, order: 1 },
            { name: "targetRange", type: "string", list_status: false, order: 2 },
        ];
        this.listsToCheck = [
            {
                name: "customRangeFormatting",
                type: "array",
                item_type: "object",
                list_status: true,
                order: 3,
                check_sub_field: "addresses",
            },
        ];
        this.description = new ToolDescription("editFormat", "Apply workbook formatting updates (fills, fonts, borders, dimensions).", EditFormatParamsSchema);
        this.paramsSchema = EditFormatParamsSchema;
        this.systemPromptDescription = SYSTEM_PROMPT_DESCRIPTION;
        this.flushQueue = Promise.resolve();
        this.bufferedRuns = [];
        this.streamedRunnerResults = [];
        this.usedStreaming = false;
        this.streamingDisabledWarned = false;
    }
    setSessionId(sessionId) {
        this.fallbackSessionId = sessionId;
    }
    reset() {
        super.reset();
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = undefined;
        }
        this.flushQueue = Promise.resolve();
        this.bufferedRuns = [];
        this.streamedRunnerResults = [];
        this.usedStreaming = false;
        this.streamError = undefined;
        this.cachedSheetName = undefined;
        this.cachedTargetRange = undefined;
        this.streamingDisabledWarned = false;
    }
    addStep(fieldName, value) {
        super.addStep(fieldName, value);
        if (fieldName === "sheetName" && typeof value === "string") {
            const sheetName = value.trim();
            this.cachedSheetName = sheetName || undefined;
            return;
        }
        if (fieldName === "targetRange" && typeof value === "string") {
            const targetRange = value.trim();
            if (targetRange) {
                this.cachedTargetRange = targetRange;
                if (this.bufferedRuns.length > 0) {
                    this.scheduleFlush(0);
                }
            }
            return;
        }
        if (fieldName !== "customRangeFormatting" || !value || typeof value !== "object") {
            return;
        }
        if (!this.isStreamingEnabled()) {
            if (!this.streamingDisabledWarned) {
                console.log(`${STREAM_LOG_PREFIX} Streaming disabled (ENABLE_EDIT_FORMAT_STREAMING=false); ignoring incremental format runs`);
                this.streamingDisabledWarned = true;
            }
            return;
        }
        if (this.streamError) {
            return;
        }
        const normalized = normalizeCustomRangeFormatting(value);
        if (!normalized) {
            // Likely partial or empty formatting payload.
            return;
        }
        this.queueRun(normalized);
    }
    isStreamingEnabled() {
        return ENABLE_EDIT_FORMAT_STREAMING;
    }
    queueRun(run) {
        this.usedStreaming = true;
        this.bufferedRuns.push(run);
        if (this.bufferedRuns.length >= STREAM_BATCH_SIZE) {
            this.scheduleFlush(0);
        }
        else {
            this.scheduleFlush(STREAM_FLUSH_MS);
        }
    }
    scheduleFlush(delayMs) {
        if (this.flushTimer) {
            if (delayMs === 0) {
                clearTimeout(this.flushTimer);
                this.flushTimer = undefined;
            }
            else {
                return;
            }
        }
        if (delayMs === 0) {
            this.flushPendingRuns();
            return;
        }
        this.flushTimer = setTimeout(() => {
            this.flushTimer = undefined;
            this.flushPendingRuns();
        }, delayMs);
    }
    flushPendingRuns() {
        if (this.streamError || this.bufferedRuns.length === 0) {
            return;
        }
        if (!this.cachedTargetRange) {
            // Wait until targetRange stabilizes.
            return;
        }
        const toSend = this.bufferedRuns.splice(0, this.bufferedRuns.length);
        this.flushQueue = this.flushQueue.then(() => this.flushRuns(toSend));
    }
    async flushRuns(runs) {
        if (!runs.length || this.streamError) {
            return;
        }
        if (!this.cachedTargetRange) {
            return;
        }
        try {
            const result = await this.runner.editFormat({
                sheetName: this.cachedSheetName,
                targetRange: this.cachedTargetRange,
                customRangeFormatting: runs,
            }, { sessionId: this.fallbackSessionId });
            this.streamedRunnerResults.push(result);
            if (!result.success) {
                this.streamError = result.errors?.[0] || "Streaming editFormat call failed.";
                console.warn(`${STREAM_LOG_PREFIX} Flush failed: ${this.streamError}`);
            }
        }
        catch (error) {
            this.streamError = error instanceof Error ? error.message : String(error);
            console.warn(`${STREAM_LOG_PREFIX} Flush error: ${this.streamError}`);
        }
    }
    buildStreamingResult() {
        const data = [];
        const warnings = [];
        const errors = [];
        let success = true;
        let durationMs = 0;
        let operationCount = 0;
        let usedBackendFallback = false;
        let timestamp = new Date().toISOString();
        for (const result of this.streamedRunnerResults) {
            success = success && result.success;
            if (Array.isArray(result.data)) {
                data.push(...result.data);
            }
            else if (result.data !== undefined) {
                data.push(result.data);
            }
            if (result.warnings?.length)
                warnings.push(...result.warnings);
            if (result.errors?.length)
                errors.push(...result.errors);
            durationMs += result.telemetry?.durationMs ?? 0;
            operationCount += result.telemetry?.operationCount ?? 0;
            usedBackendFallback = usedBackendFallback || Boolean(result.telemetry?.usedBackendFallback);
            if (result.telemetry?.timestamp)
                timestamp = result.telemetry.timestamp;
        }
        return {
            success,
            data,
            warnings: warnings.length > 0 ? warnings : undefined,
            errors: errors.length > 0 ? errors : undefined,
            telemetry: {
                operationCount: operationCount > 0 ? operationCount : undefined,
                durationMs,
                usedBackendFallback: usedBackendFallback || undefined,
                timestamp,
            },
        };
    }
    attachWriteTracking(result, sheetName, targetRange) {
        const data = asObject(result.data);
        const effectiveSheet = sheetName || this.cachedSheetName || "Unknown";
        const effectiveRange = targetRange || this.cachedTargetRange || "FULL_SHEET";
        const allImages = [];
        const rangeImages = data.range_images_base64;
        const rangeImageNames = data.range_image_names;
        if (Array.isArray(rangeImages)) {
            for (let i = 0; i < rangeImages.length; i++) {
                allImages.push({
                    base64: rangeImages[i],
                    label: rangeImageNames?.[i] || `${effectiveSheet}_${effectiveRange}`,
                });
            }
        }
        else if (typeof data.range_image_base64 === "string") {
            allImages.push({
                base64: data.range_image_base64,
                label: `${effectiveSheet}_${effectiveRange}`,
            });
        }
        const { imageIds, rawImages } = collectImagesFromResult(allImages, "format");
        const excelReadInfos = [];
        if (imageIds.length > 0) {
            for (let i = 0; i < imageIds.length; i++) {
                excelReadInfos.push(buildWriteEntry("formatting", effectiveSheet, effectiveRange, "writeFormatting", imageIds[i]));
            }
        }
        else {
            excelReadInfos.push(buildWriteEntry("formatting", effectiveSheet, effectiveRange, "writeFormatting"));
        }
        return {
            ...result,
            _imageIds: imageIds.length > 0 ? imageIds : undefined,
            _rawImages: rawImages.length > 0 ? rawImages : undefined,
            _excelReadInfos: excelReadInfos.length > 0 ? excelReadInfos : undefined,
        };
    }
    async execute(args) {
        const sessionId = resolveSessionId(args, this.fallbackSessionId);
        const request = normalizeEditFormatRequest(unwrapRequest(args));
        const colorUsage = summarizeColorUsage(request);
        console.log(`[agent-runtime/editFormat] request session=${sessionId || "(none)"} targetRange=${request.targetRange} columnWidths=[${summarizeColumnWidths(request.columnWidths)}] rowHeights=[${summarizeRowHeights(request.rowHeights)}] colors=[${colorUsage.used}] offPaletteColors=[${colorUsage.offPalette}]`);
        if (colorUsage.offPalette !== "none") {
            console.warn(`[agent-runtime/editFormat] non-backend palette colors detected: ${colorUsage.offPalette}`);
        }
        try {
            this.cachedSheetName = request.sheetName?.trim() || undefined;
            this.cachedTargetRange = request.targetRange?.trim() || undefined;
            if (this.isStreamingEnabled() && this.usedStreaming) {
                if (this.flushTimer) {
                    clearTimeout(this.flushTimer);
                    this.flushTimer = undefined;
                }
                this.flushPendingRuns();
                await this.flushQueue;
                if (this.streamError) {
                    const partial = this.buildStreamingResult();
                    return this.attachWriteTracking({
                        ...partial,
                        success: false,
                        errors: [...(partial.errors || []), this.streamError],
                    }, request.sheetName, request.targetRange);
                }
                const hasResidualPayload = Boolean((request.columnWidths && request.columnWidths.length > 0) ||
                    (request.rowHeights && request.rowHeights.length > 0) ||
                    (request.conditionalFormatting && request.conditionalFormatting.length > 0) ||
                    request.clearConditionalFormatting !== undefined ||
                    request.copyFormatting);
                if (hasResidualPayload) {
                    const residualResult = await this.runner.editFormat({
                        sheetName: request.sheetName,
                        targetRange: request.targetRange,
                        columnWidths: request.columnWidths,
                        rowHeights: request.rowHeights,
                        conditionalFormatting: request.conditionalFormatting,
                        clearConditionalFormatting: request.clearConditionalFormatting,
                        copyFormatting: request.copyFormatting,
                        customRangeFormatting: [],
                    }, { sessionId });
                    this.streamedRunnerResults.push(residualResult);
                }
                return this.attachWriteTracking(this.buildStreamingResult(), request.sheetName, request.targetRange);
            }
            const result = await this.runner.editFormat(request, { sessionId });
            const data = asObject(result.data);
            const operations = data.operations_applied ?? data.operationsApplied ?? "n/a";
            const columnWidthsSet = data.column_widths_set ?? data.columnWidthsSet ?? "n/a";
            const rowHeightsSet = data.row_heights_set ?? data.rowHeightsSet ?? "n/a";
            console.log(`[agent-runtime/editFormat] response success=${result.success} operations=${String(operations)} column_widths_set=${String(columnWidthsSet)} row_heights_set=${String(rowHeightsSet)}`);
            if (result.errors?.length) {
                console.warn(`[agent-runtime/editFormat] errors: ${result.errors.join("; ")}`);
            }
            return this.attachWriteTracking(result, request.sheetName, request.targetRange);
        }
        catch (error) {
            console.error("[agent-runtime/editFormat] execution failed:", error);
            throw error;
        }
        finally {
            this.reset();
        }
    }
}
